import { Ionicons } from "@expo/vector-icons";
import React, { useEffect, useState } from "react";
import { View, Text, ActivityIndicator, Image } from "react-native";

import { useAppTheme } from "../../../design-system/ThemeProvider";
import { t } from "../../../i18n/translations";
import { supabase } from "../../../shared/services/supabaseService";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { useUserProfile } from "../../quiz-core/useStore";

interface PlayerStatusListProps {
  roomId: string;
}

interface RoomPlayer {
  user_id: string;
  name: string;
  finished_at?: string | null;
  avatar_url?: string | null;
}

const PlayerStatusList = ({ roomId }: PlayerStatusListProps) => {
  const { colors } = useAppTheme();
  const profile = useUserProfile();
  const { language } = useLanguageStore();
  const currentUserId = profile?.id;
  const [players, setPlayers] = useState<RoomPlayer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function fetchPlayers() {
      setLoading(true);
      const { data, error } = await supabase
        .from("multiplayer_game_room_players")
        .select("user_id, name, finished_at, avatar_url")
        .eq("room_id", roomId)
        .order("joined_at", { ascending: true });
      if (!mounted) return;
      if (error) {
        setPlayers([]);
        setLoading(false);
        return;
      }
      setPlayers(data || []);
      setLoading(false);
    }
    fetchPlayers();
    const interval = setInterval(fetchPlayers, 2500);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [roomId]);

  if (!players || players.length === 0) {
    if (loading) {
      return (
        <View style={{ alignItems: "center", width: "100%" }}>
          <ActivityIndicator color="#FFD700" size="large" style={{ marginTop: 24, marginBottom: 16 }} />
          <Text style={{ color: colors.onSurface, fontSize: 18, marginTop: 20 }}>
            {t('multiplayer_lobby.waitingForPlayers', language)}
          </Text>
        </View>
      );
    }
    return (
      <Text style={{ color: colors.onSurface, fontSize: 18, marginTop: 20 }}>
        {t('multiplayer_lobby.waitingForPlayers', language)}
      </Text>
    );
  }

  return (
    <View style={{ width: "100%", alignItems: "center", marginBottom: 28 }}>
      {loading && (
        <ActivityIndicator color="#FFD700" size="large" style={{ marginTop: 18, marginBottom: 10 }} />
      )}
      {players.map((player) => {
        const isYou = player.user_id === currentUserId;
        const isFinished = !!player.finished_at;
        return (
          <View
            key={player.user_id}
            style={{
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
              paddingVertical: 12,
              paddingHorizontal: 12,
              marginBottom: 8,
              backgroundColor: colors.surfaceContainerLow,
              borderColor: colors.outlineVariant,
              borderWidth: 1,
              borderRadius: 18,
            }}
          >
            {/* Avatar or checkmark */}
            <View style={{
              width: 38, height: 38, borderRadius: 19, backgroundColor: isYou ? colors.primaryContainer : colors.surfaceContainerHigh,
              alignItems: "center", justifyContent: "center", borderWidth: 2, borderColor: isYou ? colors.primary : colors.outlineVariant, marginRight: 12
            }}>
              {isYou ? (
                <Ionicons name="checkmark-circle" size={28} color="#4ade80" />
              ) : player.avatar_url ? (
                <Image source={{ uri: player.avatar_url }} style={{ width: 28, height: 28, borderRadius: 14 }} />
              ) : (
                <Ionicons name="person-circle" size={28} color={colors.onSurfaceVariant} />
              )}
            </View>
            {/* Name */}
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: 17, color: isYou ? colors.primary : colors.onSurface, fontWeight: "bold", marginRight: 10 }}>
                {isYou ? t('multiplayer_lobby.you', language) : player.name}
              </Text>
              <Text style={{ color: colors.onSurfaceVariant, fontSize: 13, marginTop: 2 }}>
                {isFinished ? t('multiplayer_lobby.finished', language) : t('multiplayer_waiting.othersStillAnswering', language)}
              </Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: isFinished ? colors.correctLight : colors.surfaceContainerHigh,
                borderRadius: 999,
                paddingHorizontal: 10,
                paddingVertical: 6,
                marginLeft: 10,
              }}
            >
              <Ionicons
                name={isFinished ? "checkmark-circle" : "time-outline"}
                size={16}
                color={isFinished ? "#4ade80" : colors.primary}
                style={{ marginRight: 6 }}
              />
              <Text style={{ color: isFinished ? "#15803d" : colors.onSurface, fontSize: 13, fontWeight: "700" }}>
                {isFinished ? t('multiplayer_lobby.finished', language) : t('multiplayer_lobby.waiting', language)}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default PlayerStatusList;
